import { FormattedFloatInput, Select, SelectMultiple } from "@msys/ui";
import { Autocomplete, TextField } from "@mui/material";
import React from "react";
import { useTranslate } from "@tolgee/react";
import type { EntitySearchFilterOperator } from "../../../clients/graphqlTypes.js";
import type {
  SearchFilterEntryBuilder,
  SearchFilterPropertySlot,
} from "./searchFilterSlots.js";
import {
  searchFilterAuthoredValues,
  searchFilterDataType,
  searchFilterOperator,
} from "./searchFilterSlots.js";
import { NamedRangeFilterInput } from "./NamedSearchFilterValueInput.js";

/** One selectable value of a filtered property, with its display label. */
export type SearchFilterValueOption = { value: string; label: string };

/** A free-form row suggests nothing; the identity keeps the list referentially stable. */
const EMPTY_FREE_FORM_OPTIONS: string[] = [];

/**
 * What a filter row may be set to. The options are the property's own allowed
 * values — its full set — rather than the values that happen to occur among
 * currently-matching templates, which is what the filter-set modal's
 * availability facet offers. An empty option list means the property is
 * free-form, so the row takes a typed value instead of a choice.
 */
export type SearchFilterValueOptions =
  | { kind: "bool" }
  | {
      kind: "number" | "numberArray";
      options: SearchFilterValueOption[];
      unit?: string | null;
    }
  | { kind: "text" | "textArray"; options: SearchFilterValueOption[] };

interface Props<TEntry> {
  slot: SearchFilterPropertySlot;
  valueOptions: SearchFilterValueOptions;
  label: string;
  disabled?: boolean;
  /**
   * Turn the values the row now holds into the caller's edit shape —
   * `searchFilterEntry` to persist a write entry, `authoredFilterFragment` to
   * buffer a fragment.
   */
  buildEntry: SearchFilterEntryBuilder<TEntry>;
  onChange: (entry: TEntry) => Promise<void>;
}

/**
 * How many values the row's operator takes — the control follows the operator,
 * not just the property kind, so a number filter can be `< 100`, `between` two
 * bounds, or one of a list.
 */
function operatorArity(
  operator: EntitySearchFilterOperator
): "single" | "range" | "many" {
  if (operator === "between") return "range";
  if (operator === "in" || operator === "anyOf" || operator === "allOf") {
    return "many";
  }
  return "single";
}

/** The authored value control of one filter row, writing under its operator. */
export function SearchFilterValueInput<TEntry>({
  slot,
  valueOptions,
  label,
  disabled = false,
  buildEntry,
  onChange,
}: Props<TEntry>) {
  const { t } = useTranslate(["Global"]);
  const operator = searchFilterOperator(slot);
  const current = searchFilterAuthoredValues(slot);
  const write = (
    values: ReadonlyArray<string | number | boolean>,
    under: EntitySearchFilterOperator = operator
  ) =>
    onChange(buildEntry(slot.key, searchFilterDataType(slot), under, values));

  const isNumber =
    valueOptions.kind === "number" || valueOptions.kind === "numberArray";
  const arity = operatorArity(operator);

  if (valueOptions.kind === "bool") {
    const boolOptions = [
      { label: t("Yes", { ns: "Global" }), value: "true" },
      { label: t("No", { ns: "Global" }), value: "false" },
    ];
    // `in` compares against a list, so a bool row under it has to keep both
    // values — the scalar control would drop all but the first.
    return arity === "many" ? (
      <SelectMultiple
        label={label}
        options={boolOptions}
        value={current.map(String)}
        disabled={disabled}
        onChange={values => void write(values)}
      />
    ) : (
      <Select
        label={label}
        options={boolOptions}
        value={current.length > 0 ? String(current[0]) : ""}
        disabled={disabled}
        clearable
        onClear={() => void write([])}
        onChange={value => void write(value === "" ? [] : [value === "true"])}
      />
    );
  }

  if (arity === "range") {
    const [min = null, max = null] = current.map(Number);
    return (
      <NamedRangeFilterInput
        label={label}
        disabled={disabled}
        min={Number.isNaN(min) ? null : min}
        max={Number.isNaN(max) ? null : max}
        onChange={async ({ min: nextMin, max: nextMax }) => {
          // Clearing one bound narrows the filter to the other side rather
          // than dropping it, as the list-price row does — writing no values
          // would leave a `between` with nothing to match on.
          if (nextMin !== null && nextMax !== null) {
            await write([nextMin, nextMax]);
          } else if (nextMax !== null) {
            await write([nextMax], "lte");
          } else if (nextMin !== null) {
            await write([nextMin], "gte");
          } else {
            await write([]);
          }
        }}
      />
    );
  }

  // Free-form property: no allowed values to choose from, so take typed ones.
  if (valueOptions.options.length === 0) {
    // A membership operator still takes a list here, so the row collects the
    // typed values as chips rather than collapsing them to a single one.
    if (arity === "many") {
      return (
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          clearOnBlur
          selectOnFocus
          handleHomeEndKeys
          options={EMPTY_FREE_FORM_OPTIONS}
          value={current.map(String)}
          disabled={disabled}
          onChange={(_event, values) => void write(values)}
          renderInput={params => (
            <TextField {...params} label={label} size="small" />
          )}
        />
      );
    }

    const single = current.length > 0 ? String(current[0]) : "";
    return isNumber ? (
      <FormattedFloatInput
        label={label}
        value={single === "" ? null : Number(single)}
        disabled={disabled}
        onChange={value => void write(value === null ? [] : [value])}
      />
    ) : (
      <TextField
        label={label}
        size="small"
        value={single}
        disabled={disabled}
        onChange={event =>
          void write(event.target.value === "" ? [] : [event.target.value])
        }
      />
    );
  }

  if (arity === "single") {
    return (
      <Select
        label={label}
        options={valueOptions.options}
        value={current.length > 0 ? String(current[0]) : ""}
        disabled={disabled}
        clearable
        onClear={() => void write([])}
        onChange={value => void write(value === "" ? [] : [value])}
      />
    );
  }

  return (
    <SelectMultiple
      label={label}
      options={valueOptions.options}
      value={current.map(String)}
      disabled={disabled}
      onChange={values => void write(values)}
    />
  );
}
